import React, { useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import {
  Voting,
  VotingResult,
  VotingResultsContainer,
  VotingSlide,
  VotingVote,
} from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type VotingProviderProps = {
  interaction: Voting;
  interactionResults: VotingResultsContainer;
  preview: boolean;
  children?: React.ReactNode;
};

export type VotingContextProps = {
  votes: VotingVote[];
  preview: boolean;
  participated: boolean;
  registered: boolean;
  addVotes: (slide: VotingSlide, votes: VotingVote[]) => void;
  register: () => void;
  submit: () => void;
  slideVoted: (slideId: string) => boolean;
  finished: () => boolean;
  resolvePercent: (slide: VotingSlide, optionId: string) => number | undefined;
};

const VotingContext = React.createContext<VotingContextProps>({
  participated: false,
  registered: false,
  preview: false,
  slideVoted: () => false,
  finished: () => false,
  addVotes: () => null,
  submit: () => null,
  resolvePercent: () => undefined,
  register: () => null,
  votes: [],
});

const VotingProvider: React.FC<VotingProviderProps> = (
  props: VotingProviderProps
): JSX.Element => {
  const { interaction, interactionResults, preview } = props;
  const initialVotes: VotingVote[] = !preview
    ? LocalStorageService.getVotes(interaction.id) || []
    : [];
  const initialHasParticipated: boolean = !preview
    ? LocalStorageService.getParticipation(interaction.id)
    : false;

  const [participated, setParticipated] = useState<boolean>(
    initialHasParticipated
  );
  const [registered, setRegistered] = useState<boolean>(false);
  const [votes, setVotes] = useState<VotingVote[]>(initialVotes);
  const api = useMemo<InteractionApi>(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  const slideVoted = (slideId: string): boolean => {
    if (!votes) {
      return false;
    }
    return !!votes.find(
      (knownVote: VotingVote) => knownVote.slideId === slideId
    );
  };

  const finished = (): boolean => {
    const votingOutput = interaction.slides.reduce(
      (acc: any, slide: VotingSlide): any => {
        const currVotes = votes.filter(
          (vote: VotingVote) => vote.slideId === slide.id
        );
        acc.push({
          slideId: slide.id,
          isVoted: slide.voteCount
            ? currVotes.length === slide.voteCount
            : false,
        });
        return acc;
      },
      []
    );

    return !votingOutput.find((output: any) => !output.isVoted);
  };

  const addVotes = (slide: VotingSlide, slideVotes: VotingVote[]): void => {
    // Hint: This filter is required for changing votes before submit.
    // The solution is simple, the entire slide votes are always overwritten.
    // For this purpose, the previous slide votes are filtered out here.
    const allVotes = votes.filter(
      (vote: VotingVote) => vote.slideId !== slide.id
    );

    const mergedVotes = [...allVotes, ...slideVotes];

    if (!preview) {
      LocalStorageService.setVotes(interaction.id, mergedVotes);
      TrackingService.getInstance().trackOnVote();
    }

    setVotes(mergedVotes);
  };

  const submit = (): void => {
    if (!props.preview) {
      const options: VotingResult[] = votes.map((vote: VotingVote) => {
        return { ...vote, value: 1 };
      });
      api
        .postVotingResults(interaction.id, options, !participated)
        .then(() => LocalStorageService.setParticipation(interaction.id, true));
    }
    setParticipated(true);
  };

  const isUserVote = (optionId: string): boolean =>
    votes.some((vote: VotingVote) => vote.optionId === optionId);

  const filterResultsBySlideId = (slideId: string): VotingResult[] =>
    interactionResults.results.filter(
      (result: VotingResult) => result.slideId === slideId
    );

  const resolvePercent = (
    slide: VotingSlide,
    optionId: string
  ): number | undefined => {
    const localStorageIsUserAnswer = isUserVote(optionId);
    const slideResults = filterResultsBySlideId(slide.id);
    const slideVotes = votes.filter(
      (vote: VotingVote) => vote.slideId === slide.id
    );
    const localStorageHasParticipated = slideVotes.length === slide.voteCount;

    return getVotingOptionVotesPercent(
      slideResults,
      optionId,
      slide.voteCount,
      localStorageHasParticipated,
      localStorageIsUserAnswer
    );
  };

  const register = () => setRegistered(true);

  const value = React.useMemo(
    () => ({
      slideVoted,
      finished,
      addVotes,
      submit,
      resolvePercent,
      register,
      participated,
      registered,
      preview,
      votes,
    }),
    [participated, registered, votes, preview]
  );

  return (
    <VotingContext.Provider value={value}>
      {props.children}
    </VotingContext.Provider>
  );
};

const getVotingOptionVotesPercent = (
  slideResults: VotingResult[],
  optionId: string,
  slideVoteCount: number,
  localStorageHasParticipated: boolean,
  localStorageIsUserAnswer: boolean
): number => {
  const optionResults: VotingResult[] = slideResults.filter(
    (result: VotingResult) => result.optionId === optionId
  );
  const optionResult: VotingResult | undefined =
    optionResults.length > 0 ? optionResults[0] : undefined;
  let optionVotes: number = optionResult ? optionResult.value : 0;
  let totalVotes = slideResults
    .map((r: VotingResult) => r.value)
    .reduce((a: number, b: number): number => a + b, 0);

  // Maybe a counting problem:
  // Assume: If you are the first participant, the results are empty.
  // If you click on an option, you must see "100% of the participants pick your answer"
  // Problem:
  // - you come back or reload the page and
  // - the caching time is expired and
  // - your answer is now in the official results response
  // => then your answer will be counted twice and the percentage is wrong
  if (localStorageIsUserAnswer) {
    // Your vote is not in the result, because of cached results (cloudfront)
    // Plus user vote from local storage
    optionVotes += 1;
  }

  if (localStorageHasParticipated) {
    // Your vote is not in the result, because of cached results (cloudfront)
    // Plus voteCount from local storage
    totalVotes += slideVoteCount;
  }

  // Because slideVoteCount is the same count for EVERY participant, we can divide by that.
  // So we can interpolate: 1 vote = 1 participant
  totalVotes = totalVotes / slideVoteCount;

  return Math.round((optionVotes / totalVotes) * 100);
};

const VotingConsumer = VotingContext.Consumer;

export { VotingConsumer, VotingProvider };
export default VotingContext;
